/**
 * Stop history — every time this AI Service was stopped, by whom, and how it
 * ended (task 1164). On the Overview tab, above the Danger Zone.
 *
 * The rows are the service's `EndpointStop` ledger (task 1163), most recent
 * first; Start closes a row and never removes it. An automatic row expands to
 * the breaker's evidence: the failure count, the last error, and a link to each
 * failed run in the Run Explorer.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';
import { UserName } from '@/components/ui/UserName';
import { useEndpointStops } from '@/hooks/useEndpoints';
import type { EndpointStop } from '@/types';
import { formatStopInstant, NO_STOP_REASON } from './serviceStop';

const KIND_LABELS: Record<EndpointStop['kind'], string> = {
  manual: 'Manual',
  auto: 'Automatic',
};

const KIND_CLASSES: Record<EndpointStop['kind'], string> = {
  manual: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  auto: 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200',
};

const byMostRecent = (a: EndpointStop, b: EndpointStop) =>
  new Date(b.stoppedAt).getTime() - new Date(a.stoppedAt).getTime();

interface ServiceStopHistoryCardProps {
  endpointId: string;
}

export function ServiceStopHistoryCard({ endpointId }: ServiceStopHistoryCardProps) {
  const { data, isLoading, isError } = useEndpointStops(endpointId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // The service orders them already; sorting again keeps the card right if it
  // ever does not.
  const stops = [...(data ?? [])].sort(byMostRecent);

  return (
    <section
      data-testid="service-stop-history"
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="flex items-center text-sm font-semibold text-gray-900 dark:text-white">
        <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
        Stop history
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Every time this service was stopped, by whom, and how it ended.
      </p>

      {isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading stop history…</p>
      ) : isError ? (
        <p data-testid="service-stop-history-error" className="mt-4 text-sm text-red-600 dark:text-red-400">
          Could not load the stop history.
        </p>
      ) : stops.length === 0 ? (
        <p data-testid="service-stop-history-empty" className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No stop history
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Kind</th>
                <th className="py-2 pr-4">Started</th>
                <th className="py-2 pr-4">Ended</th>
                <th className="py-2">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm dark:divide-gray-700">
              {stops.map((stop) => {
                const automatic = stop.kind === 'auto';
                const expanded = expandedId === stop.id;
                const runIds = stop.collaborationIds ?? [];
                return (
                  <Fragment key={stop.id}>
                    <tr data-testid={`service-stop-row-${stop.id}`} className="align-top">
                      <td className="py-3 pr-4">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${KIND_CLASSES[stop.kind]}`}>
                          {KIND_LABELS[stop.kind]}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                        <p>{formatStopInstant(stop.stoppedAt)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          by <UserName userId={stop.stoppedBy} />
                        </p>
                      </td>
                      <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                        {stop.endedAt ? (
                          <>
                            <p>{formatStopInstant(stop.endedAt)}</p>
                            {stop.endedBy && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                started by <UserName userId={stop.endedBy} />
                              </p>
                            )}
                          </>
                        ) : (
                          <span
                            data-testid={`service-stop-active-${stop.id}`}
                            className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                          >
                            Active
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-gray-700 dark:text-gray-300">
                        <p>{stop.reason?.trim() || NO_STOP_REASON}</p>
                        {automatic && (
                          <button
                            type="button"
                            data-testid={`service-stop-evidence-toggle-${stop.id}`}
                            onClick={() => setExpandedId(expanded ? null : stop.id)}
                            className="mt-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                          >
                            {expanded ? 'Hide evidence' : 'Show evidence'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {automatic && expanded && (
                      <tr data-testid={`service-stop-evidence-${stop.id}`}>
                        <td colSpan={4} className="pb-4">
                          <div className="space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                            <p>
                              <span className="font-semibold">Consecutive failures:</span>{' '}
                              {stop.failureCount ?? runIds.length}
                            </p>
                            <p>
                              <span className="font-semibold">Last error:</span>{' '}
                              <span className="font-mono">{stop.lastError ?? 'not recorded'}</span>
                            </p>
                            {runIds.length > 0 && (
                              <p>
                                <span className="font-semibold">Failed runs:</span>{' '}
                                {runIds.map((id, index) => (
                                  <span key={id}>
                                    {index > 0 && ' · '}
                                    <Link
                                      to={serviceRunPath(endpointId, id)}
                                      className="font-mono font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                                    >
                                      {id.slice(0, 8)}
                                    </Link>
                                  </span>
                                ))}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default ServiceStopHistoryCard;
